import {
  Box,
  easing,
  Fade,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  playerSessionsSelectors,
  selectUserAudienceRegions,
  selectUserCompliance,
  uiActions,
  useFetchRecommendedEpisodesQuery,
} from '@/store';
import { Referrer } from '@/types';

import { EpisodeCard, EpisodeCardVariants } from '../EpisodeCard';

interface YourNextListenProps {
  referrer: Referrer;
  transitionDuration?: number;
  compact?: boolean;
}

export const YourNextListen = ({
  referrer,
  transitionDuration = 0,
  compact = false,
}: YourNextListenProps) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const compliance = useSelector(selectUserCompliance);
  const audienceRegions = useSelector(selectUserAudienceRegions);

  const playerSessions = useSelector(playerSessionsSelectors.selectEntities);

  const { data: recommendations } = useFetchRecommendedEpisodesQuery({
    compliance,
    audienceRegions,
  });

  const nextEpisodes = useMemo(
    () =>
      recommendations?.filter(
        recommendation =>
          !playerSessions[recommendation.episodeId] ||
          playerSessions[recommendation.episodeId].status === 'none',
      ),
    [recommendations, playerSessions],
  );

  return (
    <Box>
      {!(isMobile || compact) && (
        <Fade
          in
          timeout={{ enter: transitionDuration }}
          style={{
            transitionDelay: `${transitionDuration}ms`,
          }}
          easing={{
            enter: easing.easeOut,
          }}>
          <Typography variant="h4" sx={{ mb: 1.5 }}>
            Your Next Listen
          </Typography>
        </Fade>
      )}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nextEpisodes?.slice(0, 3).map((e, index) => (
            <EpisodeCard
              key={e.episodeId}
              episode={e}
              onLinkClick={() => dispatch(uiActions.closeActiveModal())}
              referrer={{ ...referrer, position: index + 1 }}
              variant={EpisodeCardVariants.SMALL}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { md: 'row', xs: 'column' },
            alignItems: compact ? { md: 'baseline', xs: 'center' } : 'center',
          }}>
          {nextEpisodes?.slice(0, 3).map((e, index) => (
            <Fade
              key={e.episodeId}
              in
              timeout={{ enter: transitionDuration }}
              style={{
                transitionDelay: `${transitionDuration + index * 250}ms`,
              }}
              easing={{
                enter: easing.easeOut,
              }}>
              <Box>
                <EpisodeCard
                  episode={e}
                  onLinkClick={() => dispatch(uiActions.closeActiveModal())}
                  referrer={{ ...referrer, position: index + 1 }}
                  variant={
                    compact
                      ? EpisodeCardVariants.MEDIUM
                      : EpisodeCardVariants.LARGE
                  }
                />
              </Box>
            </Fade>
          ))}
        </Box>
      )}
    </Box>
  );
};
